import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Button,
  Snackbar,
  Alert,
  Divider,
  Avatar,
  Fade,
  useMediaQuery,
  Stack,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  PersonAdd as ReferralIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Group as GroupIcon,
  TrendingUp as TrendingIcon,
  Share as ShareIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  MoreVert as MoreIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  Link as LinkIcon,
  Check as CheckIcon,
  Handshake as HandshakeIcon,
  ContactSupport as ContactIcon,
  Public as PublicIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';
import { alpha, ThemeProvider, createTheme } from '@mui/material/styles';
import client from '../API';
import useAuth from '../Hooks/use-auth';
import { useCreatorAuth } from '../Hooks/creator-use-auth';

// MODULAR IMPORTS
import ReferralDetailDialog from './ReferralCommission/ReferralDetailDialog'; // NEW: Import modular component
import CommissionsSection from './CommissionsSection'; // NEW: Import commissions section

/** -------- Clipboard -------- */
async function copyTextToClipboard(text) {
  try {
    if (
      typeof navigator !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === 'function' &&
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      typeof document !== 'undefined' &&
      document.hasFocus()
    ) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-9999px';
    document.body.appendChild(ta);

    const selection = document.getSelection();
    const originalRange =
      selection && selection.rangeCount ? selection.getRangeAt(0) : null;

    ta.select();
    const ok = document.execCommand('copy');

    document.body.removeChild(ta);
    if (originalRange) {
      selection.removeAllRanges();
      selection.addRange(originalRange);
    }
    if (ok) return true;
  } catch { /* fall through */ }

  window.prompt('Copy this invite link:', text);
  return false;
}

/** -------- Theme -------- */
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#6366f1', light: '#818cf8', dark: '#4f46e5' },
    secondary: { main: '#ec4899' },
    background: { default: '#f8fafc', paper: '#ffffff' },
    text: { primary: '#0f172a', secondary: '#64748b' },
    success: { main: '#10b981', light: '#34d399', dark: '#059669' },
    info: { main: '#3b82f6' },
  },
  shape: { borderRadius: 16 },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  ],
});

/** -------- Helpers -------- */
const safePick = (obj, key) => obj?.[key] ?? obj?.data?.[key];
const fmtMoney = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

/** -------- Invite Card -------- */
function InviteLinkCard({ accountType, link, loading, copied, onGenerate, onCopy, onShare }) {
  const isBrand = accountType === 'brand';
  const icon = isBrand ? <BusinessIcon /> : <StarIcon />;
  const title = isBrand ? 'Invite Brands' : 'Invite Creators';
  const description = isBrand 
    ? 'Companies looking to hire creators'
    : 'Content creators looking to monetize';

  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        transition: 'all 0.2s ease-in-out',
        border: `1.5px solid ${alpha('#6366f1', 0.12)}`,
        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {icon}
          <Box>
            <Typography variant="h6" fontWeight="bold">{title}</Typography>
            <Typography variant="body2" color="text.secondary">{description}</Typography>
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {!link ? (
          <Button
            fullWidth
            variant="contained"
            onClick={() => onGenerate(accountType)}
            disabled={loading}
            startIcon={<LinkIcon />}
            sx={{ borderRadius: 2, py: 1.5 }}
          >
            {loading ? 'Generating...' : 'Generate Link'}
          </Button>
        ) : (
          <Fade in>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Your invite link:
              </Typography>
              <TextField
                fullWidth
                value={link}
                variant="outlined"
                size="small"
                InputProps={{
                  readOnly: true,
                  sx: { 
                    fontSize: '0.875rem',
                    fontFamily: 'monospace',
                    '& .MuiOutlinedInput-input': { padding: '8px 12px' }
                  },
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy">
                        <IconButton
                          onClick={() => onCopy(link, accountType)}
                          color={copied ? 'success' : 'primary'}
                          size="small"
                        >
                          {copied ? <CheckIcon /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Share">
                        <IconButton
                          onClick={() => onShare(link)}
                          size="small"
                          sx={{ ml: 0.5 }}
                        >
                          <ShareIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="outlined"
                onClick={() => onGenerate(accountType)}
                size="small"
                sx={{ borderRadius: 1.5 }}
              >
                Generate New Link
              </Button>
            </Box>
          </Fade>
        )}
      </CardContent>
    </Card>
  );
}

/** -------- Access Denied Dialog -------- */
function AccessDeniedDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha('#f59e0b', 0.1),
            }}
          >
            <MoneyIcon sx={{ fontSize: 28, color: '#f59e0b' }} />
          </Box>
          <Typography variant="h6" fontWeight="bold">Partner Access Required</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              This feature is only available to Blitz Partners
            </Typography>
            <Typography variant="body2" color="text.secondary">
              As a Blitz Partner, you'll be able to view detailed financial activity and track earnings from your referral network.
            </Typography>
          </Alert>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: alpha('#10b981', 0.03) }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
              Partner Benefits Include:
            </Typography>
            <Stack spacing={1} sx={{ mt: 1.5, ml: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="body2">View detailed referral financial activity</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="body2">Track commission earnings in real-time</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="body2">Access invoice and payout histories</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                <Typography variant="body2">Earn commissions on network activity</Typography>
              </Box>
            </Stack>
          </Paper>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%)',
              border: `1.5px solid ${alpha('#ec4899', 0.2)}`,
            }}
          >
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              Interested in becoming a partner?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Contact our partnerships team to learn more about joining the Blitz Partner Program and unlocking commission earnings.
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<ContactIcon />}
              href="mailto:partnerships@useblitz.co?subject=Partner Program Inquiry"
              sx={{
                background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                fontWeight: 600,
                px: 2.5,
                '&:hover': {
                  background: 'linear-gradient(135deg, #db2777 0%, #9333ea 100%)',
                },
              }}
            >
              Contact Partnerships Team
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 1.5 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function MyReferralsPage() {
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { isAuthorized: isUserAuthorized, user: regularUser } = useAuth();
  const { isAuthorized: isCreatorAuthorized, user: creatorUser, isInitialized } = useCreatorAuth();

  const currentUserId = useMemo(() => {
    if (isUserAuthorized && regularUser?.globalUserId) return regularUser.globalUserId;
    if (isCreatorAuthorized && creatorUser?.globalUserId) return creatorUser.globalUserId;
    return null;
  }, [isUserAuthorized, isCreatorAuthorized, regularUser, creatorUser]);

  const [inviteLinks, setInviteLinks] = useState({ brand: '', creator: '' });
  const [copied, setCopied] = useState({ brand: false, creator: false });
  const [selectedReferralId, setSelectedReferralId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [accessDeniedDialog, setAccessDeniedDialog] = useState(false);

  const { data: basicReferralsData, isLoading: isLoadingBasic } = useQuery(
    ['ref-basic'],
    () => client.globalUsers.getReferrals(),
    { staleTime: 1000 * 60 * 5 }
  );

  const basicStats = basicReferralsData?.data ?? basicReferralsData ?? {};
  const totalReferrals = basicStats.total_referrals || 0;
  const completedReferrals = basicStats.completed_referrals || 0;

  const { data: activityData, isLoading: isLoadingActivity } = useQuery(
    ['ref-activity'],
    () => client.globalUsers.getReferralActivity({ limit: 50, offset: 0, include_access: true }),
    { 
      staleTime: 1000 * 60 * 5,
      enabled: !isLoadingBasic && !!basicReferralsData
    }
  );

  const activity = activityData?.data ?? activityData ?? {};
  const items = activity.items || [];
  const overallTotals = activity.overall_totals || {};
  
  const hasSpecificAccess = (overallTotals.active_commission_grants || 0) > 0;
  const hasAllAccess = overallTotals.has_all_access || false;
  const hasAnyCommissionAccess = hasSpecificAccess || hasAllAccess;
  const totalPotentialCommissions = overallTotals.potential_commissions || 0;
  
  const totalEarnings = useMemo(() => {
    if (!hasAnyCommissionAccess) return 0;
    return items.reduce((sum, item) => {
      const payout = item?.financial_totals?.payouts_amount_sum || 0;
      return sum + Number(payout);
    }, 0);
  }, [items, hasAnyCommissionAccess]);

  const isEligibleForPartnership = !isLoadingActivity && completedReferrals >= 2 && !hasAnyCommissionAccess;
  const isLoading = isLoadingBasic;

  const { mutateAsync: generateInvite, isLoading: generating } = useMutation(
    async (accountType) => {
      const res = await client.globalUsers.copyInvite(accountType);
      const success = safePick(res, 'success');
      const inviteUrl = safePick(res, 'invite_url') || safePick(res, 'inviteUrl') || '';
      if (!success || !inviteUrl) {
        const err = safePick(res, 'error') || 'Failed to generate invite link';
        throw new Error(err);
      }
      return { accountType, inviteUrl };
    },
    {
      onSuccess: ({ accountType, inviteUrl }) => {
        setInviteLinks((prev) => ({ ...prev, [accountType]: inviteUrl }));
        setSnackbar({ open: true, message: `${accountType === 'brand' ? 'Brand' : 'Creator'} invite link generated!`, severity: 'success' });
      },
      onError: (error) => {
        setSnackbar({ open: true, message: error?.message || 'Failed to generate invite link', severity: 'error' });
      }
    }
  );

  const handleGenerateLink = async (accountType) => {
    await generateInvite(accountType);
  };

  const handleCopyInviteLink = async (link, accountType) => {
    const ok = await copyTextToClipboard(link);
    setCopied((prev) => ({ ...prev, [accountType]: ok }));
    setSnackbar({
      open: true,
      message: ok ? 'Link copied to clipboard!' : 'Please copy the link manually',
      severity: ok ? 'success' : 'warning'
    });
    if (ok) {
      setTimeout(() => setCopied((prev) => ({ ...prev, [accountType]: false })), 2000);
    }
  };

  const handleShareAny = async (url) => {
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join me on Blitz', text: "Use my invite link—let's grow together!", url });
        return;
      } catch (e) {
        if (e?.name === 'AbortError') return;
      }
    }
    const ok = await copyTextToClipboard(url);
    setSnackbar({
      open: true,
      message: ok ? 'Invite link copied to clipboard!' : 'Please copy the link manually',
      severity: ok ? 'success' : 'warning'
    });
  };

  const fallbackReferralLink = useMemo(() => {
    if (!currentUserId) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/invite/${currentUserId}`;
  }, [currentUserId]);

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  const handleReferralClick = (referralId) => {
    if (hasAllAccess) {
      setSelectedReferralId(referralId);
      setDetailOpen(true);
      return;
    }
    
    if (!hasAnyCommissionAccess) {
      setAccessDeniedDialog(true);
      return;
    }
    
    const referralItem = items.find(item => item.referral?.id === referralId);
    if (referralItem?.referral?.access?.has_commission_rights) {
      setSelectedReferralId(referralId);
      setDetailOpen(true);
    } else {
      setAccessDeniedDialog(true);
    }
  };

  if (!isInitialized) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
            <Box textAlign="center">
              <LinearProgress sx={{ width: { xs: 280, sm: 400 }, mb: 3, height: { xs: 6, sm: 8 }, borderRadius: 4 }} />
              <Typography color="text.secondary" variant={isMobile ? 'body1' : 'h6'}>Initializing...</Typography>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  if (isLoading) {
    return (
      <ThemeProvider theme={lightTheme}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4, md: 6 } }}>
          <Box display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
            <Box textAlign="center">
              <LinearProgress sx={{ width: { xs: 280, sm: 400 }, mb: 3, height: { xs: 6, sm: 8 }, borderRadius: 4 }} />
              <Typography color="text.secondary" variant={isMobile ? 'body1' : 'h6'}>Loading your referrals...</Typography>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={lightTheme}>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 3, sm: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Fade in timeout={600}>
            <Box>
              {/* Header & Invite Links (existing code) */}
              <Box textAlign="center" mb={{ xs: 4, sm: 6 }} px={{ xs: 1, sm: 0 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: 64, sm: 80 },
                    height: { xs: 64, sm: 80 },
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    mb: 3,
                    boxShadow: '0 10px 30px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <ReferralIcon sx={{ fontSize: { xs: 36, sm: 44 }, color: 'white' }} />
                </Box>

                <Typography variant={isMobile ? 'h3' : isTablet ? 'h2' : 'h1'} component="h1"
                  sx={{ fontWeight: 800, color: 'text.primary', mb: 2, letterSpacing: '-0.02rem' }}>
                  Referral Program
                  {hasAllAccess && (
                    <Chip
                      icon={<PublicIcon />}
                      label="All-Access Partner"
                      color="success"
                      sx={{ ml: 2, fontWeight: 700, fontSize: '0.875rem' }}
                    />
                  )}
                </Typography>

                <Typography variant={isMobile ? 'body1' : 'h6'} color="text.secondary"
                  sx={{ maxWidth: { xs: '100%', sm: 700 }, mx: 'auto', px: { xs: 2, sm: 0 }, mb: 4 }}>
                  {hasAllAccess 
                    ? 'As an All-Access Partner, you earn commissions on all your referrals\' activity.'
                    : 'Generate brand or creator invite links and earn rewards when they join and activate.'
                  }
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <InviteLinkCard
                      accountType="brand"
                      link={inviteLinks.brand}
                      loading={generating}
                      copied={copied.brand}
                      onGenerate={handleGenerateLink}
                      onCopy={handleCopyInviteLink}
                      onShare={handleShareAny}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <InviteLinkCard
                      accountType="creator"
                      link={inviteLinks.creator}
                      loading={generating}
                      copied={copied.creator}
                      onGenerate={handleGenerateLink}
                      onCopy={handleCopyInviteLink}
                      onShare={handleShareAny}
                    />
                  </Grid>
                </Grid>

                {!inviteLinks.brand && !inviteLinks.creator && fallbackReferralLink && (
                  <Paper 
                    elevation={0}
                    sx={{ 
                      mt: 3,
                      p: { xs: 2.5, sm: 3 },
                      maxWidth: 700,
                      mx: 'auto',
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.08) 100%)',
                      border: `2px solid ${alpha('#6366f1', 0.1)}`,
                      borderRadius: 3,
                      backdropFilter: 'blur(10px)',
                    }}
                  >
                    <Typography variant="overline" sx={{ color: '#6366f1', fontWeight: 700, letterSpacing: 1, display: 'block', mb: 2 }}>
                      Quick Link (fallback)
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="stretch">
                      <Paper
                        elevation={0}
                        sx={{
                          flex: 1, px: 2.5, py: 2, bgcolor: 'white',
                          border: `1px solid ${alpha('#000', 0.08)}`, borderRadius: 2, display: 'flex', alignItems: 'center',
                        }}
                      >
                        <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace', color: 'text.secondary' }}>
                          {fallbackReferralLink}
                        </Typography>
                      </Paper>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          startIcon={<CopyIcon />}
                          onClick={() => handleCopyInviteLink(fallbackReferralLink, 'creator')}
                          sx={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                            fontWeight: 600, px: 3, py: 1.5,
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                            '&:hover': { background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)' },
                          }}
                        >
                          Copy
                        </Button>
                        <Tooltip title="Share">
                          <IconButton
                            onClick={() => handleShareAny(fallbackReferralLink)}
                            sx={{
                              border: `2px solid ${alpha('#6366f1', 0.2)}`,
                              bgcolor: 'white',
                              '&:hover': { bgcolor: alpha('#6366f1', 0.05), borderColor: '#6366f1' },
                            }}
                          >
                            <ShareIcon sx={{ color: '#6366f1' }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Paper>
                )}
              </Box>

              {isEligibleForPartnership && (
                <Fade in>
                  <Paper
                    elevation={0}
                    sx={{
                      mb: 4,
                      p: { xs: 3, sm: 4 },
                      background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%)',
                      border: `2px solid ${alpha('#ec4899', 0.3)}`,
                      borderRadius: 3,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                          boxShadow: '0 8px 16px rgba(236, 72, 153, 0.3)',
                        }}
                      >
                        <HandshakeIcon sx={{ fontSize: 32, color: 'white' }} />
                      </Box>
                      <Box flex={1}>
                        <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                          You're Eligible for the Blitz Partner Program! 🎉
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          With {completedReferrals} active referrals, you qualify for commission earnings on your network's activity.
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<ContactIcon />}
                      sx={{
                        background: 'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
                        fontWeight: 700,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: '0 4px 12px rgba(236, 72, 153, 0.4)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #db2777 0%, #9333ea 100%)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 6px 16px rgba(236, 72, 153, 0.5)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                      href="mailto:partnerships@useblitz.co?subject=Partner Program Application"
                    >
                      Contact Us to Join
                    </Button>
                  </Paper>
                </Fade>
              )}

              {/* Stats Cards */}
              <Grid container spacing={{ xs: 2, sm: 3 }} mb={{ xs: 4, sm: 6 }}>
                <Grid item xs={12} sm={hasAnyCommissionAccess ? 4 : 6}>
                  <Card elevation={0} sx={{
                    height: '100%', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white', position: 'relative', overflow: 'hidden',
                    '&::before': { content: '""', position: 'absolute', top: 0, right: 0, width: 150, height: 150,
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      borderRadius: '50%', transform: 'translate(30%, -30%)' }
                  }}>
                    <CardContent sx={{ py: { xs: 3, sm: 4 }, position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                          Total Referrals
                        </Typography>
                        <GroupIcon sx={{ fontSize: 32, opacity: 0.8 }} />
                      </Box>
                      {isLoadingBasic ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>Loading...</Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 800, mb: 0.5 }}>
                            {totalReferrals}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>All time referrals</Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={hasAnyCommissionAccess ? 4 : 6}>
                  <Card elevation={0} sx={{
                    height: '100%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white', position: 'relative', overflow: 'hidden',
                    '&::before': { content: '""', position: 'absolute', top: 0, right: 0, width: 150, height: 150,
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      borderRadius: '50%', transform: 'translate(30%, -30%)' }
                  }}>
                    <CardContent sx={{ py: { xs: 3, sm: 4 }, position: 'relative', zIndex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                          Active Referrals
                        </Typography>
                        <CheckCircleIcon sx={{ fontSize: 32, opacity: 0.8 }} />
                      </Box>
                      {isLoadingBasic ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <CircularProgress size={24} sx={{ color: 'white' }} />
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>Loading...</Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 800, mb: 0.5 }}>
                            {completedReferrals}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>Completed / active</Typography>
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {hasAnyCommissionAccess && (
                  <Grid item xs={12} sm={4}>
                    <Card elevation={0} sx={{
                      height: '100%', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                      color: 'white', position: 'relative', overflow: 'hidden',
                      '&::before': { content: '""', position: 'absolute', top: 0, right: 0, width: 150, height: 150,
                        background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                        borderRadius: '50%', transform: 'translate(30%, -30%)' }
                    }}>
                      <CardContent sx={{ py: { xs: 3, sm: 4 }, position: 'relative', zIndex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                            Total Earnings
                          </Typography>
                          <MoneyIcon sx={{ fontSize: 32, opacity: 0.8 }} />
                        </Box>
                        {isLoadingActivity ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <CircularProgress size={24} sx={{ color: 'white' }} />
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Calculating...</Typography>
                          </Box>
                        ) : (
                          <>
                            <Typography variant={isMobile ? 'h3' : 'h2'} sx={{ fontWeight: 800, mb: 0.5 }}>
                              {fmtMoney(totalEarnings)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              {hasAllAccess ? 'All referrals payouts' : 'Lifetime payouts'}
                            </Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>

              {/* Referrals List */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Your Referrals
                  </Typography>
                  <Chip 
                    label={`${items.length} listed`} 
                    sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', fontWeight: 700 }}
                    icon={hasAllAccess ? <PublicIcon /> : undefined}
                  />
                </Box>

                {isLoadingActivity ? (
                  <Box display="flex" justifyContent="center" py={8}>
                    <Box textAlign="center">
                      <CircularProgress size={48} sx={{ mb: 2 }} />
                      <Typography color="text.secondary">Loading referral details...</Typography>
                    </Box>
                  </Box>
                ) : items.length === 0 ? (
                  <Paper 
                    elevation={0}
                    sx={{
                      p: { xs: 4, sm: 6, md: 8 }, textAlign: 'center', borderRadius: 4,
                      background: 'linear-gradient(135deg, rgba(99,102,241,0.03) 0%, rgba(139,92,246,0.03) 100%)',
                      border: `2px dashed ${alpha('#6366f1', 0.2)}`
                    }}
                  >
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: { xs: 80, sm: 100 }, height: { xs: 80, sm: 100 },
                      borderRadius: '24px', bgcolor: alpha('#6366f1', 0.1), mb: 3,
                    }}>
                      <ReferralIcon sx={{ fontSize: { xs: 40, sm: 50 }, color: '#6366f1' }} />
                    </Box>
                    <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                      No referrals yet
                    </Typography>
                    <Typography color="text.secondary" variant="body1" sx={{ maxWidth: 400, mx: 'auto' }}>
                      Generate and share brand or creator invite links to start earning rewards!
                    </Typography>
                  </Paper>
                ) : (
                  <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                    {items.map(({ referral, endpoint, financial_totals }, index) => {
                      const isActive = referral?.status === 'completed';
                      const nameFromReferral = [referral?.first_name, referral?.last_name].filter(Boolean).join(' ');
                      const nameFromEndpoint = endpoint?.linked_user
                        ? `${endpoint.linked_user.first_name} ${endpoint.linked_user.last_name}`
                        : endpoint?.linked_creator?.username;
                      const userName = nameFromReferral || nameFromEndpoint || referral?.email || endpoint?.global_user?.username || 'Anonymous User';
                      const userEmail = referral?.email || endpoint?.global_user?.email || '';
                      const joinDate = referral?.date_completed || referral?.date_generated;
                      const earnings = financial_totals?.payouts_amount_sum || 0;
                      
                      const canViewDetails = hasAllAccess || referral?.access?.has_commission_rights;

                      return (
                        <Grid item xs={12} sm={6} lg={4} key={referral?.id || index}>
                          <Card
                            elevation={0}
                            onClick={() => handleReferralClick(referral?.id)}
                            sx={{
                              cursor: 'pointer',
                              height: '100%',
                              border: `2px solid ${isActive ? alpha('#10b981', 0.2) : alpha('#000', 0.06)}`,
                              borderRadius: 3,
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              position: 'relative',
                              overflow: 'hidden',
                              bgcolor: 'white',
                              '&:hover': {
                                transform: 'translateY(-8px)',
                                boxShadow: isActive
                                  ? '0 20px 40px rgba(16, 185, 129, 0.15)'
                                  : '0 20px 40px rgba(0, 0, 0, 0.08)',
                                borderColor: isActive ? '#10b981' : alpha('#6366f1', 0.3),
                              },
                              '&::before': isActive
                                ? {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '4px',
                                    background: 'linear-gradient(90deg, #10b981 0%, #059669 100%)',
                                  }
                                : {},
                            }}
                          >
                            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2.5 }}>
                                <Avatar
                                  sx={{
                                    width: { xs: 56, sm: 64 },
                                    height: { xs: 56, sm: 64 },
                                    background: isActive
                                      ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                                      : 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)',
                                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                    fontWeight: 700,
                                    boxShadow: isActive
                                      ? '0 8px 16px rgba(16, 185, 129, 0.3)'
                                      : '0 8px 16px rgba(0, 0, 0, 0.1)',
                                  }}
                                >
                                  {String(userName).charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0, ml: 2 }}>
                                  <Typography
                                    variant="h6"
                                    sx={{
                                      fontWeight: 700,
                                      mb: 0.5,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      color: 'text.primary',
                                      fontSize: { xs: '1rem', sm: '1.125rem' },
                                    }}
                                  >
                                    {userName}
                                  </Typography>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Chip
                                      icon={isActive ? <CheckCircleIcon sx={{ fontSize: 16 }} /> : undefined}
                                      label={isActive ? 'Active' : 'Pending'}
                                      size="small"
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: '0.75rem',
                                        height: 24,
                                        bgcolor: isActive ? alpha('#10b981', 0.1) : alpha('#64748b', 0.1),
                                        color: isActive ? '#059669' : '#64748b',
                                        border: `1.5px solid ${isActive ? alpha('#10b981', 0.3) : alpha('#64748b', 0.2)}`,
                                        '& .MuiChip-icon': {
                                          color: isActive ? '#059669' : '#64748b',
                                        },
                                      }}
                                    />
                                    {canViewDetails && (
                                      <Tooltip title={hasAllAccess ? "All-Access Partner" : "You have partner access"}>
                                        <Chip
                                          icon={hasAllAccess ? <PublicIcon sx={{ fontSize: 14 }} /> : <LockIcon sx={{ fontSize: 14 }} />}
                                          label={hasAllAccess ? "All" : "Access"}
                                          size="small"
                                          sx={{
                                            fontWeight: 700,
                                            fontSize: '0.7rem',
                                            height: 24,
                                            bgcolor: alpha('#10b981', 0.1),
                                            color: '#059669',
                                            border: `1.5px solid ${alpha('#10b981', 0.3)}`,
                                          }}
                                        />
                                      </Tooltip>
                                    )}
                                  </Stack>
                                </Box>
                                <Tooltip title="More options">
                                  <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { bgcolor: alpha('#6366f1', 0.08) } }}>
                                    <MoreIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>

                              {userEmail && (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 2,
                                    p: 1.5,
                                    bgcolor: alpha('#6366f1', 0.04),
                                    borderRadius: 2,
                                  }}
                                >
                                  <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.secondary',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      fontSize: '0.875rem',
                                    }}
                                  >
                                    {userEmail}
                                  </Typography>
                                </Box>
                              )}

                              <Divider sx={{ my: 2 }} />

                              <Stack spacing={1.5}>
                                {joinDate && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1.5,
                                      bgcolor: alpha('#000', 0.02),
                                      borderRadius: 2,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: 36,
                                          height: 36,
                                          borderRadius: 2,
                                          bgcolor: alpha('#3b82f6', 0.1),
                                        }}
                                      >
                                        <CalendarIcon sx={{ fontSize: 18, color: '#3b82f6' }} />
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.secondary',
                                            display: 'block',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          {isActive ? 'Completed' : 'Created'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                          {fmtDate(joinDate)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </Box>
                                )}

                                {canViewDetails && (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      p: 1.5,
                                      bgcolor: alpha('#f59e0b', 0.08),
                                      borderRadius: 2,
                                      border: `1.5px solid ${alpha('#f59e0b', 0.2)}`,
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: 36,
                                          height: 36,
                                          borderRadius: 2,
                                          bgcolor: alpha('#f59e0b', 0.15),
                                        }}
                                      >
                                        <MoneyIcon sx={{ fontSize: 18, color: '#d97706' }} />
                                      </Box>
                                      <Box>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: 'text.secondary',
                                            display: 'block',
                                            fontSize: '0.7rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                          }}
                                        >
                                          Payouts Sum
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#d97706', fontSize: '1.25rem' }}>
                                          {fmtMoney(earnings)}
                                        </Typography>
                                      </Box>
                                    </Box>
                                    {earnings > 50 && (
                                      <Chip
                                        icon={<TrendingIcon sx={{ fontSize: 14 }} />}
                                        label="Top"
                                        size="small"
                                        sx={{
                                          bgcolor: alpha('#f59e0b', 0.15),
                                          color: '#d97706',
                                          fontWeight: 700,
                                          fontSize: '0.7rem',
                                          height: 24,
                                          '& .MuiChip-icon': { color: '#d97706' },
                                        }}
                                      />
                                    )}
                                  </Box>
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>

              {/* ========== NEW: COMMISSIONS SECTION ========== */}
              {hasAnyCommissionAccess && (
                <Box mt={8}>
                  <Divider sx={{ mb: 4 }} />
                  <Typography 
                    variant={isMobile ? 'h4' : 'h3'} 
                    sx={{ fontWeight: 700, mb: 4, color: 'text.primary' }}
                  >
                    💰 Your Commissions
                  </Typography>
                  <CommissionsSection />
                </Box>
              )}
              {/* ========== END: COMMISSIONS SECTION ========== */}

            </Box>
          </Fade>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2, fontWeight: 600 }} variant="filled">
              {snackbar.message}
            </Alert>
          </Snackbar>

          {/* MODULAR: ReferralDetailDialog */}
          <ReferralDetailDialog
            open={detailOpen}
            referralId={selectedReferralId}
            onClose={() => { setDetailOpen(false); setSelectedReferralId(null); }}
          />

          <AccessDeniedDialog
            open={accessDeniedDialog}
            onClose={() => setAccessDeniedDialog(false)}
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
}